import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSubscription } from "@/context/SubscriptionContext";
import { useLang } from "@/context/LanguageContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Check, Sparkles, Crown, Zap, Building2, Loader2, ShieldCheck, ArrowRight } from "lucide-react";

// Two categories of plans:
//  - Property-level (per listing boost) — user must go to My Listings to pick a property
//  - User-level (Broker / Builder) — subscribe once, benefits apply to the whole account
const PROPERTY_PLANS = [
  {
    key: "premium_monthly",
    icon: Sparkles,
    label_en: "Premium (Monthly)",
    label_hi: "प्रीमियम (मासिक)",
    days: 30,
    price_inr: 299,
    accent: "cream",
    highlights_en: [
      "Boost ONE property for 30 days",
      "Property shown on Homepage",
      "Verified Premium badge",
      "Priority in search results",
    ],
    highlights_hi: [
      "एक प्रॉपर्टी 30 दिन के लिए बूस्ट",
      "होमपेज पर दिखेगी",
      "वेरिफ़ाइड प्रीमियम बैज",
      "सर्च रिज़ल्ट में प्राथमिकता",
    ],
  },
  {
    key: "premium_yearly",
    icon: Crown,
    label_en: "Premium (Yearly)",
    label_hi: "प्रीमियम (वार्षिक)",
    days: 365,
    price_inr: 999,
    accent: "gold",
    popular: true,
    highlights_en: [
      "Boost ONE property for 12 months",
      "Save 72% vs monthly",
      "Homepage + Verified badge",
      "Featured across search & Vastu filter",
    ],
    highlights_hi: [
      "एक प्रॉपर्टी 12 महीनों के लिए बूस्ट",
      "मासिक की तुलना में 72% बचत",
      "होमपेज + वेरिफ़ाइड बैज",
      "सर्च और वास्तु फ़िल्टर में फ़ीचर्ड",
    ],
  },
];

const USER_PLANS = [
  {
    key: "broker_yearly",
    icon: Zap,
    label_en: "Broker Plan (Yearly)",
    label_hi: "ब्रोकर प्लान (वार्षिक)",
    days: 365,
    price_inr: 6999,
    accent: "terracotta",
    highlights_en: [
      "Unlimited property posting",
      "Featured in Brokers section on Homepage",
      "Verified badge after KYC approval",
      "Direct WhatsApp support",
    ],
    highlights_hi: [
      "अनगिनत प्रॉपर्टी पोस्टिंग",
      "होमपेज के ब्रोकर सेक्शन में फ़ीचर्ड",
      "KYC मंज़ूरी के बाद वेरिफ़ाइड बैज",
      "डायरेक्ट WhatsApp सपोर्ट",
    ],
  },
  {
    key: "builder_monthly",
    icon: Building2,
    label_en: "Builder Plan (Monthly)",
    label_hi: "बिल्डर प्लान (मासिक)",
    days: 30,
    price_inr: 7000,
    accent: "sage",
    highlights_en: [
      "Unlimited property posting",
      "ALL your listings auto-Premium",
      "Builder brand banner on Homepage",
      "Verified badge on every listing",
      "Priority KYC + dedicated manager",
    ],
    highlights_hi: [
      "अनगिनत प्रॉपर्टी पोस्टिंग",
      "आपकी सभी लिस्टिंग स्वचालित प्रीमियम",
      "होमपेज पर बिल्डर बैनर",
      "हर लिस्टिंग पर वेरिफ़ाइड बैज",
      "प्राथमिकता KYC + डेडिकेटेड मैनेजर",
    ],
  },
];

const ACCENT = {
  sage: "border-emerald-300 bg-emerald-50",
  cream: "border-border bg-card",
  gold: "border-secondary bg-secondary/10 shadow-xl scale-[1.02]",
  terracotta: "border-primary bg-primary/5",
};

export default function Plans() {
  const { user, loading: authLoading } = useAuth();
  const { subscription, refetch, isBroker, isBuilder, daysLeft } = useSubscription();
  const { lang } = useLang();
  const nav = useNavigate();
  const [processingPlan, setProcessingPlan] = useState(null);
  const [rzpReady, setRzpReady] = useState(false);

  useEffect(() => {
    if (window.Razorpay) { setRzpReady(true); return; }
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => setRzpReady(true);
    s.onerror = () => setRzpReady(false);
    document.body.appendChild(s);
  }, []);

  if (authLoading) return <div className="py-20 text-center text-muted-foreground">Loading…</div>;

  const buyUserPlan = async (planKey) => {
    if (!user) {
      nav("/login", { state: { from: "/plans" } });
      return;
    }
    setProcessingPlan(planKey);
    try {
      const { data: order } = await api.post("/subscriptions/order", { plan: planKey });
      if (order.mock) {
        await api.post("/subscriptions/verify", {
          order_id: order.order_id,
          razorpay_payment_id: `MOCK_PAY_${Date.now()}`,
          razorpay_signature: "mock-signature",
        });
        toast.success(lang === "hi" ? "मॉक भुगतान सफल — प्लान सक्रिय!" : "Mock payment success — plan active!");
        await refetch();
        nav("/");
        return;
      }
      if (!window.Razorpay) {
        toast.error("Payment system loading — try again in a moment.");
        return;
      }
      const meta = USER_PLANS.find((p) => p.key === planKey);
      const rzp = new window.Razorpay({
        key: order.key_id,
        amount: order.amount,
        currency: order.currency || "INR",
        name: "BhoomiMitra",
        description: meta?.label_en,
        order_id: order.order_id,
        prefill: { name: user.name || "", email: user.email, contact: user.phone || "" },
        theme: { color: "#B84A2E" },
        handler: async (rzpRes) => {
          try {
            await api.post("/subscriptions/verify", {
              order_id: rzpRes.razorpay_order_id,
              razorpay_payment_id: rzpRes.razorpay_payment_id,
              razorpay_signature: rzpRes.razorpay_signature,
            });
            toast.success(lang === "hi" ? "भुगतान सफल — प्लान सक्रिय!" : "Payment success — plan active!");
            await refetch();
            nav("/");
          } catch (e) {
            toast.error(formatDetail(e.response?.data?.detail) || "Verification failed");
          }
        },
        modal: { ondismiss: () => setProcessingPlan(null) },
      });
      rzp.on("payment.failed", (resp) => {
        toast.error(resp.error?.description || "Payment failed");
        setProcessingPlan(null);
      });
      rzp.open();
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail));
      setProcessingPlan(null);
    }
  };

  const currentPlanLabel = () => {
    const meta = USER_PLANS.find((p) => p.key === subscription?.plan);
    return meta ? (lang === "hi" ? meta.label_hi : meta.label_en) : subscription?.plan;
  };

  const renderCard = (p, isUserPlan) => {
    const Icon = p.icon;
    const isCurrent = subscription?.plan === p.key && subscription.status === "active";
    const isProcessing = processingPlan === p.key;
    const label = lang === "hi" ? p.label_hi : p.label_en;
    const priceLabel = `₹${p.price_inr}`;
    const perDay = (p.price_inr / p.days).toFixed(1);

    return (
      <Card key={p.key} className={`p-5 relative border-2 transition-all ${ACCENT[p.accent]}`} data-testid={`plan-card-${p.key}`}>
        {p.popular && (
          <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-secondary text-secondary-foreground text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full shadow">
            {lang === "hi" ? "सबसे लोकप्रिय" : "Most Popular"}
          </span>
        )}
        <Icon size={28} className={
          p.accent === "gold" ? "text-secondary" :
          p.accent === "terracotta" ? "text-primary" :
          p.accent === "sage" ? "text-emerald-700" :
          "text-foreground"
        } />
        <div className="text-lg font-bold mt-3">{label}</div>
        <div className="flex items-baseline gap-1 mt-1">
          <span className="text-3xl font-bold">{priceLabel}</span>
          <span className="text-xs text-muted-foreground">/{p.days} {lang === "hi" ? "दिन" : "days"}</span>
        </div>
        <div className="text-[10px] text-muted-foreground italic">≈ ₹{perDay}/{lang === "hi" ? "दिन" : "day"}</div>

        <ul className="space-y-2 mt-4 text-xs">
          {(lang === "hi" ? p.highlights_hi : p.highlights_en).map((h, i) => (
            <li key={i} className="flex items-start gap-1.5">
              <Check size={12} className="text-emerald-700 mt-[3px] shrink-0" />
              <span>{h}</span>
            </li>
          ))}
        </ul>

        <div className="mt-5">
          {isCurrent ? (
            <Badge className="w-full justify-center py-2 bg-emerald-100 text-emerald-800 border-emerald-300">
              {lang === "hi" ? "सक्रिय ✓" : "Active ✓"}
            </Badge>
          ) : isUserPlan ? (
            <Button
              className={`w-full rounded-full ${
                p.accent === "terracotta" ? "bg-primary hover:bg-primary/90" :
                p.accent === "sage" ? "bg-emerald-700 hover:bg-emerald-800 text-white" :
                "bg-foreground hover:bg-foreground/90 text-background"
              }`}
              onClick={() => buyUserPlan(p.key)}
              disabled={isProcessing || !rzpReady}
              data-testid={`buy-${p.key}-btn`}
            >
              {isProcessing ? <Loader2 className="animate-spin mr-1" size={14} /> : null}
              {lang === "hi" ? "अभी लें" : "Subscribe"}
            </Button>
          ) : (
            <Button
              variant="outline"
              className="w-full rounded-full"
              onClick={() => nav("/my-listings")}
              data-testid={`goto-boost-${p.key}-btn`}
            >
              {lang === "hi" ? "मेरी लिस्टिंग से बूस्ट करें" : "Boost from My Listings"}
              <ArrowRight size={14} className="ml-1" />
            </Button>
          )}
        </div>
      </Card>
    );
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      {/* Hero */}
      <div className="text-center mb-8">
        <div className="overline text-secondary">Pricing</div>
        <h1 className="text-3xl sm:text-5xl font-bold tracking-tight mt-2">
          {lang === "hi" ? "अपना प्लान चुनें" : "Choose Your Plan"}
        </h1>
        <p className="text-sm text-muted-foreground mt-3 max-w-2xl mx-auto">
          {lang === "hi"
            ? "प्रॉपर्टी लिस्ट करना 100% मुफ़्त। बूस्ट या सब्सक्रिप्शन पूरी तरह वैकल्पिक।"
            : "Listing properties is 100% FREE. Boost & subscriptions are completely optional."}
        </p>

        {(isBroker || isBuilder) && (
          <div className="mt-6 inline-flex items-center gap-2 bg-emerald-100 text-emerald-800 px-4 py-2 rounded-full text-sm font-semibold" data-testid="current-plan-badge">
            <ShieldCheck size={14} />
            {lang === "hi" ? "आपका प्लान:" : "Your plan:"} <span className="font-bold">{currentPlanLabel()}</span>
            {daysLeft != null && <span className="opacity-80">• {daysLeft} {lang === "hi" ? "दिन बाक़ी" : "days left"}</span>}
          </div>
        )}
      </div>

      {/* Free tier banner */}
      <Card className="p-5 bg-emerald-50 border-emerald-300 border-2 mb-10 flex flex-col sm:flex-row items-center gap-4 justify-between">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-emerald-800">
            {lang === "hi" ? "मुफ़्त टियर" : "Free Tier"}
          </div>
          <div className="text-lg font-bold mt-1">
            {lang === "hi" ? "अनगिनत लिस्टिंग — बिल्कुल मुफ़्त" : "Unlimited listings — completely free"}
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            {lang === "hi"
              ? "हर कोई प्रॉपर्टी लिस्ट कर सकता है। बूस्ट लेने पर होमपेज पर दिखेगी।"
              : "Anyone can list properties. Boost a listing to appear on Homepage."}
          </div>
        </div>
        <Button
          size="lg"
          className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-full px-6"
          onClick={() => nav("/sell")}
          data-testid="free-list-btn"
        >
          {lang === "hi" ? "मुफ़्त लिस्ट करें" : "List for Free"}
          <ArrowRight size={14} className="ml-1" />
        </Button>
      </Card>

      {/* Per-property Premium Plans */}
      <div className="mb-4">
        <div className="overline text-secondary text-center">Per-Property Boost</div>
        <h2 className="text-xl sm:text-2xl font-bold text-center mt-1">
          {lang === "hi" ? "एक लिस्टिंग को बूस्ट करें" : "Boost a Single Listing"}
        </h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-3xl mx-auto mb-14">
        {PROPERTY_PLANS.map((p) => renderCard(p, false))}
      </div>

      {/* User-level Plans */}
      <div className="mb-4">
        <div className="overline text-secondary text-center">Account-Level Plans</div>
        <h2 className="text-xl sm:text-2xl font-bold text-center mt-1">
          {lang === "hi" ? "ब्रोकर और बिल्डर के लिए" : "For Brokers & Builders"}
        </h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-3xl mx-auto">
        {USER_PLANS.map((p) => renderCard(p, true))}
      </div>

      <p className="text-center text-xs text-muted-foreground mt-10 max-w-2xl mx-auto">
        {lang === "hi"
          ? "सभी भुगतान Razorpay के जरिए सुरक्षित हैं। कोई छिपा शुल्क नहीं। कोई ऑटो-रिन्यूअल नहीं।"
          : "All payments securely processed via Razorpay. No hidden fees. No auto-renewal."}
      </p>
      <p className="text-center text-xs text-muted-foreground mt-2">
        <Link to="/terms" className="underline">{lang === "hi" ? "नियम" : "Terms"}</Link> {" • "}
        <Link to="/privacy" className="underline">{lang === "hi" ? "गोपनीयता" : "Privacy"}</Link>
      </p>
    </div>
  );
}
